import React, { useState, useRef, useEffect } from 'react';
import Webcam from 'react-webcam';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import '../../styles/PhotoBooth.css';
import '../../styles/TMRWMember/2Photobooth.css';

function PhotoBoothTMRWMember() {
  const webcamRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const existingImages = location.state?.existingImages || [];
  const selectedFrame = location.state?.selectedFrame
  const [capturedImages, setCapturedImages] = useState(existingImages);
  const [countdown, setCountdown] = useState(10);
  const [takingPhotos, setTakingPhotos] = useState(false);
  const [photoCount, setPhotoCount] = useState(0);
  const [flashVisible, setFlashVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [intervalTime, setIntervalTime] = useState(5); // Default to 5 seconds
  const [viewHeight, setViewHeight] = useState(window.innerHeight);
  const [showingStartMessage, setShowingStartMessage] = useState(false);
  const overlaySequence = selectedFrame.overlays; // Use selected frame's overlays

  // Update dimensions on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setViewHeight(window.innerHeight);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let interval;

    if (takingPhotos && countdown > 0) {
      interval = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    }

    if (countdown === 0 && photoCount < 4) {
      triggerFlash();
      capture();
      setCountdown(intervalTime);  // Reset countdown based on the selected interval
      setPhotoCount(prev => prev + 1);
    }

    if (photoCount === 4) {
      clearInterval(interval);
      setTakingPhotos(false);
      setTimeout(() => {
        if (existingImages.length > 0) {
          // If we have existing images, go to selection screen with all 8 photos
          navigate('/select/TMRWMember', { state: { images: capturedImages, selectedFrame } });
        } else {
          // If first session, go to confirmation screen
          navigate('/confirm/TMRWMember', { state: { images: capturedImages, selectedFrame } });
        }
      }, 500);
    }

    return () => clearInterval(interval);
  }, [countdown, takingPhotos, photoCount, capturedImages, navigate, intervalTime]);

  // Auto-start photo session when returning with existing images
  useEffect(() => {
    if (existingImages.length > 0) {
      setShowingStartMessage(true);
      // Start photo session automatically after a short delay
      const timer = setTimeout(() => {
        setShowingStartMessage(false);
        startPhotoSession();
      }, 1000); // 1 second delay to allow UI to load
      
      return () => {
        clearTimeout(timer);
        setShowingStartMessage(false);
      };
    }
  }, [existingImages]);

  const capture = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImages(prev => [...prev, imageSrc]);
  };

  const triggerFlash = () => {
    setFlashVisible(true);
    setTimeout(() => setFlashVisible(false), 150);
  };

  const startPhotoSession = () => {
    if (existingImages.length === 0) {
      setCapturedImages([]);
    }
    setPhotoCount(0);
    setTakingPhotos(true);
    setCountdown(intervalTime); // Start countdown based on the selected interval
  };

  const handleIntervalChange = (e) => {
    setIntervalTime(Number(e.target.value)); // Update the interval based on user selection
  };

  // Calculate appropriate webcam dimensions based on device (horizontal 4:3 ratio)
  const getWebcamDimensions = () => {
    if (isMobile) {
      // Use viewport-based dimensions for mobile
      const vw = Math.min(window.innerWidth * 0.9, 480);
      return {
        width: vw,
        height: vw * 3/4  // 4:3 aspect ratio
      };
    } else {
      // 4:3 aspect ratio for desktop, horizontal
      return {
        width: 600,
        height: 450
      };
    }
  };

  const webcamDimensions = getWebcamDimensions();

  // Set video constraints to match the container's aspect ratio and size (horizontal)
  const videoConstraints = {
    facingMode: 'user',
    width: webcamDimensions.width,
    height: webcamDimensions.height,
    aspectRatio: 4/3
  };

  return (
    <div className="PhotoBooth">
      {/* Back link to frame selection page */}
      <Link to="/frame-selection/TMRWMember" className="frame-back-link">
        ← Back
      </Link>

      {/* Mobile-only Start Button at the Top */}
      {isMobile && !takingPhotos && existingImages.length === 0 && (
        <div className="mobile-start-button-wrapper">
          <button onClick={startPhotoSession} className="start-button">
            Start!
          </button>
        </div>
      )}
      {isMobile && takingPhotos &&  (
          <div className="photo-counter">
            {photoCount}/4 Photos {existingImages.length > 0 ? '' : ''}
          </div>
      )}

      <div className="photo-layout">
        <div 
          className="webcam-container"
          style={{
            width: `${webcamDimensions.width}px`,
            height: `${webcamDimensions.height}px`,
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: '#000',
            borderRadius: '18px',
          }}
        >
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            videoConstraints={videoConstraints}
            mirrored={true}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '18px',
            }}
          />
          {/* Current overlay image */}
          <img
            src={`/img/TMRWMember/${overlaySequence[Math.min(photoCount, overlaySequence.length - 1)]}`}
            alt="Overlay"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '18px',
              pointerEvents: 'none',
              zIndex: 1
            }}
          />
          {flashVisible && <div className="flash-overlay" />}
          
          {takingPhotos && (
            <div className="countdown-overlay">{countdown}</div>
          )}
        </div>

        {/* Info/Controls Section */}
        <div className="info-container">
          {!takingPhotos && !isMobile && existingImages.length === 0 && (
            <div className="controls-stack">
              <div className="interval-selector">
                <label htmlFor="interval">Select Interval:</label>
                <select id="interval" value={intervalTime} onChange={handleIntervalChange}>
                  <option value={5}>5 Seconds</option>
                  <option value={10}>10 Seconds</option>
                </select>
              </div>
              {!isMobile && !takingPhotos && (
                <button onClick={startPhotoSession} className="start-button">
                  Start!
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );  
}

export default PhotoBoothTMRWMember;